import { saveAs } from 'file-saver';
import {
  TabSettings,
  FormattedCell,
  ReportSettings
} from '../../models/report';
import { getColumnSettings, valueToCell } from './excel.formatter';
import {
  WritingOptions,
  WorkBook,
  WorkSheet,
  CellObject,
  CellAddress,
  write,
  utils
} from 'xlsx';

export interface ColumnSettings {
  width: number;
}

const WORKBOOK_OPTIONS: WritingOptions = {
  bookType: 'xlsx',
  type: 'binary'
};

export function generateExcel({ name, tabs }: ReportSettings): void {
  const workBook = getWorkBook(tabs);
  const file = getFileFromWorkBook(workBook);

  saveAs(file, name);
}

function s2ab(s: string) {
  const buf = new ArrayBuffer(s.length);
  const view = new Uint8Array(buf);

  for (let i = 0; i !== s.length; ++i) {
    view[i] = s.charCodeAt(i) & 0xff; // tslint:disable-line:no-bitwise
  }

  return buf;
}

function getFileFromWorkBook(workBook: WorkBook): Blob {
  const output = write(workBook, WORKBOOK_OPTIONS);

  return new Blob([s2ab(output)], { type: 'application/octet-stream' });
}

function getWorkBook(tabs: TabSettings[]): WorkBook {
  const workBook: WorkBook = {
    Sheets: {},
    SheetNames: [],
    Props: {}
  };

  // Add rendering tabs to the excel workbook
  tabs.forEach(({ name, header, totals, table }: TabSettings) => {
    const workSheet: WorkSheet = getWorkSheet(header, totals, table);

    workBook.Sheets[name] = workSheet;
    workBook.SheetNames.push(name);
  });

  return workBook;
}

function getWorkSheet(
  header: string[],
  totals: FormattedCell[],
  table: FormattedCell[][]
): WorkSheet {
  const sheet: WorkSheet = {};
  const columnSettings: ColumnSettings[] = [];

  if (
    header &&
    header.length &&
    totals &&
    totals.length &&
    table &&
    table.length
  ) {
    const maxRowCount = table.length + 2;
    const maxCellCount = header.length;

    // Render header of the table
    header.forEach((column, index) => {
      addCell(sheet, index, 0, {
        value: column,
        format: undefined
      } as FormattedCell);
      columnSettings[index] = getColumnSettings(
        column,
        index,
        columnSettings[index]
      );
    });

    // Render table body
    table.forEach((row, rowIndex) => {
      rowIndex += 1;

      row.forEach((column, columnIndex) => {
        addCell(sheet, columnIndex, rowIndex, column);
        columnSettings[columnIndex] = getColumnSettings(
          column.value,
          columnIndex,
          columnSettings[columnIndex]
        );
      });
    });

    // Render totals row
    totals.forEach((column, index) => {
      addCell(sheet, index, maxRowCount, column);
    });

    // Render excel worksheet
    sheet['!cols'] = <any>columnSettings;
    sheet['!ref'] = utils.encode_range(
      { r: 0, c: 0 },
      { r: maxRowCount, c: maxCellCount }
    );
  }

  return sheet;
}

function addCell(
  sheet: WorkSheet,
  cellIndex: number,
  rowIndex: number,
  formattedCell: FormattedCell
): void {
  const workSheetCell: CellObject = valueToCell(formattedCell);
  const cell: CellAddress = { c: cellIndex, r: rowIndex };
  const reference = utils.encode_cell(cell);
  sheet[reference] = workSheetCell;
}
